import React, {
  FC,
  useState,
  useCallback,
  useRef,
  useMemo,
  Fragment,
  useEffect,
  useLayoutEffect,
} from "react";
import classNames from "classnames/dedupe";
import { truncateString } from "../../../../helpers/formatters";
import useWatchState from "../../../../hooks/useWatchState";
import { FormCheckWrapper, FormLabel } from "../../../Atoms/FormLabel";
import { RadioButton } from "../../../ActionsSelectors/RadioButton";
import { AbstractFilter, IAbstractFilterProps } from "../AbstractFilter";
import FormItemWrapper from "../FilterFormItemWrapper/FilterFormItemWrapper";
import {
  IFilterUpdate,
  IRadioFilterData,
  IRadioFilterItem,
} from "../Filter.interface";
import useTheme from "../../../Utility/ThemeProvider/useTheme";
import deepClone from "../../../../helpers/deepClone";
import { IFilterTagData } from "../../FilterTags";
import { ScrollContainer } from "../../../Utility";
import { SearchInput } from "../../Search";
import { ShowMoreLess } from "../ShowMoreLess";

export interface IRadioFilterProps extends IAbstractFilterProps {
  /** Filter data items
   * @type {IRadioFilterData}
   */
  filterData: IRadioFilterData;

  /**
   * If true, selected value will be rendered along the filter category
   * in accordion title or dropdown button
   */
  isDisplaySelected?: boolean;
  /**
   *  Enable 'Show More' - 'Show Less' button and collapsing list of filter items
   * */
  hasShowMore?: boolean;
  /**
   *  If Show More is enabled - set how many items will be shown in collapsed state
   * */
  showMoreItemsLimit?: number;
  /**
   *  If Show More is enabled - set expanded-collapsed state of items list
   * */
  showMoreExpanded?: boolean;
  /**
   *  If true search input is added to filter the list
   * */
  hasSearch?: boolean;
  /**
   *  Search term passed from outside if needed
   * */
  searchTerm?: string;
  /**
   * Set translation text for labels
   */
  labels?: {
    searchPlaceholder?: string;
    showMore?: string;
    showLess?: string;
    noResultsMessage?: string;
  };
  /**
   * A callback function for when this component is changing array of checkbox inputs
   */
  onChange?(data: {
    values: IRadioFilterItem[];
    updates: IFilterUpdate[];
  }): void;
  /** A callback function for filtering items with search */
  onSearch?(value: string);
}

export const resetRadioFilter = (
  filterItem: IRadioFilterData,
): IRadioFilterData => ({
  ...filterItem,
  items: filterItem.items.map((item) => ({
    ...item,
    checked:
      filterItem?.defaultValue && item.value === filterItem?.defaultValue,
  })),
});

export const radioFilterItemToTagsDTO = (
  data: IRadioFilterData,
): IFilterTagData => {
  const checkedItem = data.items.find((item) => item.checked);
  if (
    checkedItem &&
    (!data.defaultValue || data.defaultValue !== checkedItem.value)
  ) {
    return {
      category: data.name,
      id: checkedItem.value,
      text: checkedItem.title,
    };
  }
  return null;
};

const RadioFilter: FC<IRadioFilterProps> = ({
  filterData,
  isDisplaySelected,
  variant,
  hasShowMore = false,
  showMoreItemsLimit = 5,
  showMoreExpanded = false,
  classes,
  hasSearch,
  labels,
  searchTerm = "",
  onChange,
  onSearch,
  ...abstractFilterProps
}) => {
  const theme = useTheme(variant);
  const searchInputRef = useRef(null);
  const { items: filterItems, name, categoryName, isSorting } = filterData;

  const [filterItemsState, setFilterItems] = useWatchState<IRadioFilterItem[]>({
    value: filterItems,
  });

  const [displayItemsState, setDisplayItems] = useWatchState<
    IRadioFilterItem[]
  >({
    value: filterItems,
  });

  const [searchQuery, setSearchQuery] = useWatchState<string>({
    value: searchTerm,
  });

  const [isShowMoreExpandedState, setIsExpanded] = useWatchState<boolean>({
    value: showMoreExpanded,
  });

  const [displayShowMoreState, setDisplayShowMore] = useState<boolean>(
    hasShowMore && filterItemsState.length > showMoreItemsLimit,
  );

  const onChangeHandler = useCallback(
    (event) => {
      const newSelectedValue = event.target.value;
      const updatedItems = deepClone(filterItemsState).map((item) => ({
        ...item,
        checked: item.value === newSelectedValue,
      }));

      setFilterItems(updatedItems);
      onChange?.({
        values: updatedItems,
        updates: [{ fieldName: name, value: newSelectedValue }],
      });
    },
    [filterItemsState, name, onChange, setFilterItems],
  );

  const onSearchHandler = useCallback(
    (value: string) => {
      setSearchQuery(value);
      onSearch?.(value);
    },
    [onSearch, setSearchQuery],
  );

  const onSearchClear = useCallback(() => {
    searchInputRef.current?.focus();
  }, []);

  const selectedItem = displayItemsState.find(
    (item: IRadioFilterItem) => !!item.checked,
  );

  const checkedItemTitle = filterItemsState.find(
    (item) => item.checked === true,
  )?.title;

  const filterTitle =
    isDisplaySelected && selectedItem ? `${selectedItem.title}` : categoryName;

  useEffect(() => {
    /**
     * Filtering items depending on searchQuery
     */
    let newDisplayItems = searchQuery
      ? filterItemsState.filter((item) =>
          item.title.toLowerCase().includes(searchQuery.toLowerCase()),
        )
      : filterItemsState;

    /**
     * If showMore functionality is enable - slice display items array
     */
    displayShowMoreState &&
      !isShowMoreExpandedState &&
      (newDisplayItems = newDisplayItems.slice(0, showMoreItemsLimit));

    setDisplayItems(newDisplayItems);
  }, [
    displayShowMoreState,
    filterItemsState,
    isShowMoreExpandedState,
    searchQuery,
    setDisplayItems,
    showMoreItemsLimit,
  ]);

  const radioGroupBody = useMemo(() => {
    const noResults = hasSearch ? (
      <p
        className={classNames("text-paragraph", {
          "text-color-white": theme === "dark",
          "my-6": abstractFilterProps.filterType === "accordion",
          "px-4 mb-4": abstractFilterProps.filterType !== "accordion",
        })}
        data-testid="no-search-results"
      >
        {labels?.noResultsMessage}
      </p>
    ) : null;
    return displayItemsState.length > 0
      ? displayItemsState.map((item) => {
          const className = classNames("form-check-input", {
            "form-check-input--light": theme === "light",
            "input-checked": item.checked,
          });

          return (
            <Fragment key={item.id}>
              {item.header ? (
                <div className="filter-form-item filter-form-item--text">
                  {item.header}
                </div>
              ) : null}
              <FormItemWrapper type="radio">
                <FormCheckWrapper isFilterRow>
                  <RadioButton
                    id={item.id}
                    variant={theme}
                    name={name}
                    value={item.value}
                    checked={item.checked}
                    onChange={onChangeHandler}
                    className={className}
                  />
                  <FormLabel variant={theme} htmlFor={item.id}>
                    {item.title}
                  </FormLabel>
                </FormCheckWrapper>
              </FormItemWrapper>
            </Fragment>
          );
        })
      : noResults;
  }, [
    displayItemsState,
    theme,
    name,
    onChangeHandler,
    hasSearch,
    abstractFilterProps.filterType,
    labels,
  ]);

  const isAnyOptionSelected = filterItemsState.find((item) => item.checked);

  useLayoutEffect(() => {
    setDisplayShowMore(
      hasShowMore && filterItemsState.length > showMoreItemsLimit,
    );
  }, [filterItemsState, hasShowMore, showMoreItemsLimit]);

  return (
    <AbstractFilter
      classes={classes}
      variant={theme}
      subtitle={checkedItemTitle}
      title={truncateString(filterTitle, 18)}
      badge={{ count: isAnyOptionSelected && !isSorting && 1 }}
      {...abstractFilterProps}
    >
      {hasSearch && (
        <FormItemWrapper type="search">
          <form className="search-container">
            <SearchInput
              searchTerm={searchQuery}
              placeholderLabel={labels?.searchPlaceholder}
              autoComplete="off"
              onChange={onSearchHandler}
              ref={searchInputRef}
              onClear={onSearchClear}
            />
          </form>
        </FormItemWrapper>
      )}
      {abstractFilterProps.filterType === "accordion" ? (
        radioGroupBody
      ) : (
        <ScrollContainer>{radioGroupBody}</ScrollContainer>
      )}
      {displayShowMoreState &&
        displayItemsState.length >= showMoreItemsLimit && (
          <ShowMoreLess
            isShown={showMoreExpanded}
            showLessLabel={labels?.showLess}
            showMoreLabel={labels?.showMore}
            onChange={setIsExpanded}
          />
        )}
    </AbstractFilter>
  );
};

export default RadioFilter;
